import { disconnectPrasaran } from "@certa/prasaran";
import { basename } from "@certa/network";
import {
  ToastPlacements,
  ToastTypes,
  showToast
} from "@certa/catalyst/components/Toast";
import { intl } from "main/src/modules/common/components/IntlCapture";
import {
  STORAGE_KEYS_TO_PRESERVE,
  STORAGE_PREFIX
} from "../constants/logout.constants";
import { FEATURE_HIGHLIGHTS_KEY } from "@certa/common/hooks/useFeatureHighlights";
import { removeUserQuery } from "@certa/queries/hooks/authV2.hooks";

const removeCookies = () => {
  const expireCookie = (key: string) => {
    document.cookie = key + " =; expires = Thu, 01 Jan 1970 00:00:00 UTC";
  };

  const cookies = document.cookie;
  const splitCookies = cookies.split(";");
  for (const cookie of splitCookies) {
    const key = cookie.split("=");
    expireCookie(key[0]);
  }
};

/**
 * Clears localStorage items while preserving specific items
 * Handles both prefixed (e.g., "certa.key") and non-prefixed keys
 */
const clearLocalStorage = (keysToKeepWithoutPrefix: string[]) => {
  const getPropKey = (prop: string): string => {
    if (prop.startsWith(`${STORAGE_PREFIX}.`)) {
      return prop;
    }
    return `${STORAGE_PREFIX}.${prop}`;
  };

  const keysToKeepWithPrefix = keysToKeepWithoutPrefix.map(getPropKey);

  const valuesToKeep = keysToKeepWithPrefix.map(key =>
    localStorage.getItem(key)
  );

  localStorage.clear();

  // Restore preserved items
  valuesToKeep.forEach((value, index) => {
    if (value !== null) {
      localStorage.setItem(keysToKeepWithPrefix[index], value);
    }
  });
};

type PostLogoutActionOptions = {
  /** If true, includes current URL as 'next' parameter in login redirect */
  addNextURL?: boolean;
  /** SSO redirect URL (if provided, redirects to IdP logout) */
  redirectURL?: string;
  /** React Router navigate function for programmatic navigation */
  navigate?: (
    to: string | { pathname: string; search?: string },
    options?: { replace?: boolean }
  ) => void;
};

export const postLogoutActionV2 = ({
  addNextURL = false,
  redirectURL = "",
  navigate
}: PostLogoutActionOptions = {}) => {
  removeUserQuery();
  clearLocalStorage([...STORAGE_KEYS_TO_PRESERVE, FEATURE_HIGHLIGHTS_KEY]);

  removeCookies();

  disconnectPrasaran();

  // If the user is already on the login page, we don't need to redirect
  // and show the toast again
  // Case: When multiple API calls are made then the user is redirected
  // to the login page multiple times
  if (window.location.pathname.startsWith("/login")) {
    return;
  }

  if (redirectURL) {
    // In case of SSO, we get a redirect URL, that needs to be hit to ensure
    // user is logged out from the IdP as well.
    showToast({
      type: ToastTypes.WARNING,
      title: intl.formatMessage({
        id: "notificationInstances.loggedOutSSO",
        defaultMessage: "You're being logged out..."
      }),
      placement: ToastPlacements.BOTTOM_RIGHT,
      toastKey: "logoutSSOToast"
    });
    // SSO redirects must use window.location to navigate to external IdP
    window.location.href = redirectURL;
  } else {
    // Auth queries are triggered on / route if csrftoken exists but user session is expired
    if (window.location.pathname !== "/") {
      showToast({
        type: ToastTypes.WARNING,
        title: intl.formatMessage({
          id: "notificationInstances.loggedOut",
          defaultMessage: "You've been logged out. Redirecting to login page..."
        }),
        placement: ToastPlacements.BOTTOM_RIGHT,
        toastKey: "logoutToast"
      });
    }

    const nextUrl = window.location.search
      ? `/login/?next=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`
      : `/login/?next=${window.location.pathname}`;
    const newURL = addNextURL ? nextUrl : `/login/`;

    // Use React Router navigate if available, otherwise fall back to window.location
    if (navigate) {
      setTimeout(() => {
        navigate(`${basename}${newURL}`, { replace: true });
      }, 1000);
    } else {
      setTimeout(() => {
        window.location.href = `${basename}${newURL}`;
      }, 1000);
    }
  }
};
